import React, { useState, useEffect } from 'react'
import {
  fetchUninvoicedSalesByCustomer,
  fetchInvoicesByCustomer,
  generateInvoiceNumber,
  createInvoice,
  markSalesAsInvoiced
} from '../supabase-client'
import { getTodayISOString, parseDateString, toLocalDateString } from '../utils/dateUtils.js'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { generateConsolidatedStatement, isEligibleForConsolidatedStatement, getCustomerBranchCount } from '../utils/consolidatedStatementGenerator'
import { pdf } from '@react-pdf/renderer'
import { saveAs } from 'file-saver'
import ConsolidatedStatementTemplate from './invoice/ConsolidatedStatementTemplate'
import { generateMultiFilePDFs } from '../utils/multiFilePDFGenerator.jsx'
import { fetchFarmInformation } from '../supabase-client'

export default function CustomerInvoicingModal({
  isOpen,
  onClose,
  customer,
  onInvoiceGenerated
}) {
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [uninvoicedSales, setUninvoicedSales] = useState([])
  const [existingInvoices, setExistingInvoices] = useState([])
  const [error, setError] = useState(null)
  const [totalAmount, setTotalAmount] = useState(0)

  // Consolidated Statement State
  const [consolidatedStartDate, setConsolidatedStartDate] = useState('')
  const [consolidatedEndDate, setConsolidatedEndDate] = useState('')
  const [statementFormat, setStatementFormat] = useState('single-pdf')
  const [loadingConsolidated, setLoadingConsolidated] = useState(false)
  const [branchCount, setBranchCount] = useState(0)

  useEffect(() => {
    if (isOpen && customer) {
      loadInvoicingData()
      loadBranchCount()
      setDefaultDates()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, customer])

  const loadBranchCount = async () => {
    try {
      const count = await getCustomerBranchCount(customer.id)
      setBranchCount(count)
    } catch (err) {
      console.error('Error loading branch count:', err)
    }
  }

  const setDefaultDates = () => {
    const today = new Date()
    const endDate = toLocalDateString(today)
    const startDate = toLocalDateString(new Date(today.getFullYear(), today.getMonth(), 1))
    setConsolidatedStartDate(startDate)
    setConsolidatedEndDate(endDate)
  }

  const loadInvoicingData = async () => {
    setLoading(true)
    setError(null)
    try {
      // Fetch uninvoiced sales for this customer (including disregarded ones)
      const salesData = await fetchUninvoicedSalesByCustomer(customer.id, true)
      setUninvoicedSales(salesData)

      // Calculate total amount (excluding disregarded sales)
      const total = salesData.reduce((sum, sale) => {
        if (sale.disregarded) return sum  // Skip disregarded sales
        const saleTotal = sale.sale_items?.reduce((itemSum, item) =>
          itemSum + parseFloat(item.sub_total || 0), 0) || 0
        return sum + saleTotal
      }, 0)
      setTotalAmount(total)

      // Fetch existing invoices for this customer
      const invoicesData = await fetchInvoicesByCustomer(customer.id)
      setExistingInvoices(invoicesData)
    } catch (err) {
      console.error('Error loading invoicing data:', err)
      setError('Unable to load invoicing data. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleGenerateInvoice = async () => {
    setGenerating(true)
    setError(null)

    try {
      const todayDate = getTodayISOString()

      // Group sales by branch (excluding disregarded sales)
      const salesByBranch = {}
      uninvoicedSales.forEach(sale => {
        // Skip disregarded sales from invoice generation
        if (sale.disregarded) return

        const branchKey = sale.branch_id || 'main'
        if (!salesByBranch[branchKey]) {
          salesByBranch[branchKey] = {
            branchId: sale.branch_id,
            branchName: sale.branch?.name || 'Main',
            sales: [],
            total: 0
          }
        }

        const saleTotal = sale.sale_items?.reduce((sum, item) =>
          sum + parseFloat(item.sub_total || 0), 0) || 0

        salesByBranch[branchKey].sales.push(sale)
        salesByBranch[branchKey].total += saleTotal
      })

      // Create invoices for each branch
      for (const [_branchKey, branchData] of Object.entries(salesByBranch)) {
        if (branchData.sales.length > 0) {
          // Generate a new sequential invoice number for this branch
          const invoiceNumber = await generateInvoiceNumber()

          // Create the invoice record
          const invoice = await createInvoice({
            invoice_no: invoiceNumber,
            customer_id: customer.id,
            branch_id: branchData.branchId,
            date: todayDate,
            total: branchData.total,
            pdf_url: null,
            sent: false
          })

          // Mark sales as invoiced
          await markSalesAsInvoiced(
            branchData.sales.map(sale => sale.id),
            invoice.id
          )
        }
      }

      // Reload data to show updated state
      await loadInvoicingData()

      // Notify parent component if needed
      if (onInvoiceGenerated) {
        onInvoiceGenerated()
      }

      // Show success message
      setError(null)
    } catch (err) {
      console.error('Error generating invoice:', err)
      setError('Unable to generate invoice. Please try again.')
    } finally {
      setGenerating(false)
    }
  }

  const handleGenerateConsolidatedStatement = async () => {
    setLoadingConsolidated(true)
    setError(null)

    try {
      // Validate dates
      if (!consolidatedStartDate || !consolidatedEndDate) {
        setError('Please select both start and end dates')
        return
      }

      if (new Date(consolidatedStartDate) > new Date(consolidatedEndDate)) {
        setError('Start date must be before or equal to end date')
        return
      }

      // Generate consolidated statement data
      const statementData = await generateConsolidatedStatement(
        customer.id,
        consolidatedStartDate,
        consolidatedEndDate
      )

      // Fetch farm information for PDF header
      const farmInfo = await fetchFarmInformation()

      // Generate PDF based on selected format
      if (statementFormat === 'single-pdf') {
        // Generate single multi-page PDF
        const blob = await pdf(
          <ConsolidatedStatementTemplate
            statementData={statementData}
            farmInfo={farmInfo}
          />
        ).toBlob()

        // Download PDF
        const filename = `${customer.companyName || customer.name}_Consolidated_Statement_${consolidatedStartDate}_to_${consolidatedEndDate}.pdf`
        saveAs(blob, filename)
      } else {
        // Generate multiple PDFs as ZIP
        await generateMultiFilePDFs(
          statementData,
          farmInfo,
          customer.companyName || customer.name
        )
      }

      setError(null)
    } catch (err) {
      console.error('Error generating consolidated statement:', err)
      setError(`Unable to generate consolidated statement: ${err.message}`)
    } finally {
      setLoadingConsolidated(false)
    }
  }

  const formatDate = (dateString) => {
    const date = parseDateString(dateString)
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: '2-digit'
    })
  }

  const formatCurrency = (amount) => {
    return `£${parseFloat(amount).toFixed(2)}`
  }

  // Check if customer is eligible for consolidated statement
  const isEnterpriseWithMultipleBranches = customer?.type === 'enterprise' && branchCount > 1

  if (!customer) return null

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) onClose()
    }}>
      <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Customer Invoicing - {customer.companyName || customer.name}</DialogTitle>
        </DialogHeader>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="mt-4 text-muted-foreground">Loading invoicing data...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Uninvoiced Transactions Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Uninvoiced Transactions</span>
                  <Badge variant={totalAmount === 0 ? "secondary" : totalAmount < 0 ? "destructive" : "default"}>
                    Total: {formatCurrency(totalAmount)}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {uninvoicedSales.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No uninvoiced transactions for this customer
                  </div>
                ) : (
                  <div className="space-y-4">
                    {uninvoicedSales.map(sale => (
                      <div key={sale.id} className="border rounded-lg p-4 bg-muted/30">
                        <div className="flex justify-between items-center mb-3">
                          <div>
                            <span className="font-medium">Date:</span> {formatDate(sale.date)}
                            {sale.consignment_no && (
                              <span className="ml-4">
                                <span className="font-medium">Consignment:</span> {sale.consignment_no}
                              </span>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Badge variant="outline">
                              {sale.branch?.name || 'Main'}
                            </Badge>
                            {sale.disregarded && (
                              <Badge variant="destructive">
                                Disregarded
                              </Badge>
                            )}
                          </div>
                        </div>

                        <div className="space-y-2">
                          {sale.sale_items?.map(item => (
                            <div key={item.id} className="grid grid-cols-3 gap-4 text-sm">
                              <span>{item.product?.strain || item.product_type?.name || 'Unknown Product'}</span>
                              <span className="text-muted-foreground">
                                {item.quantity}kg × {formatCurrency(item.price)}
                              </span>
                              <span className="text-right font-medium">
                                {formatCurrency(item.sub_total)}
                              </span>
                            </div>
                          ))}
                        </div>

                        <div className="border-t mt-3 pt-2 text-right">
                          <span className="font-semibold">
                            Sale Total: {formatCurrency(
                              sale.sale_items?.reduce((sum, item) =>
                                sum + parseFloat(item.sub_total || 0), 0) || 0
                            )}
                          </span>
                        </div>
                      </div>
                    ))}

                    {/* Generate Invoice Button */}
                    <div className="flex justify-end pt-4">
                      <Button
                        variant="create"
                        onClick={handleGenerateInvoice}
                        disabled={generating || uninvoicedSales.length === 0}
                      >
                        {generating ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Generating...
                          </>
                        ) : (
                          <>
                            Generate Invoice
                            {totalAmount === 0 && (
                              <span className="ml-2 text-xs">(Force Create £0)</span>
                            )}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Separator />

            {/* Consolidated Statement Section (Enterprise Only) */}
            {isEnterpriseWithMultipleBranches && (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle>Consolidated Statement (Enterprise)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        Generate a comprehensive statement showing all invoices and payments across all branches for this customer.
                      </p>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="consolidatedStartDate">Start Date</Label>
                          <Input
                            id="consolidatedStartDate"
                            type="date"
                            value={consolidatedStartDate}
                            onChange={(e) => setConsolidatedStartDate(e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="consolidatedEndDate">End Date</Label>
                          <Input
                            id="consolidatedEndDate"
                            type="date"
                            value={consolidatedEndDate}
                            onChange={(e) => setConsolidatedEndDate(e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Output Format</Label>
                        <RadioGroup
                          value={statementFormat}
                          onValueChange={setStatementFormat}
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="single-pdf" id="single-pdf" />
                            <Label htmlFor="single-pdf" className="font-normal cursor-pointer">
                              Single multi-page PDF (all branches in one file)
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="multiple-pdfs" id="multiple-pdfs" />
                            <Label htmlFor="multiple-pdfs" className="font-normal cursor-pointer">
                              Multiple PDF files (one per branch, downloaded as ZIP)
                            </Label>
                          </div>
                        </RadioGroup>
                      </div>

                      <div className="flex gap-2 items-center">
                        <Button
                          onClick={handleGenerateConsolidatedStatement}
                          disabled={loadingConsolidated}
                        >
                          {loadingConsolidated ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Generating...
                            </>
                          ) : (
                            'Generate Statement'
                          )}
                        </Button>
                        {statementFormat === 'multiple-pdfs' && (
                          <p className="text-sm text-muted-foreground">
                            Files will be downloaded as a ZIP archive
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Separator />
              </>
            )}

            {/* Existing Invoices Section */}
            <Card>
              <CardHeader>
                <CardTitle>Invoice History</CardTitle>
              </CardHeader>
              <CardContent>
                {existingInvoices.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No invoices generated for this customer yet
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Invoice #</TableHead>
                        <TableHead>Branch</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {existingInvoices.map(invoice => (
                        <TableRow key={invoice.id}>
                          <TableCell>{formatDate(invoice.date)}</TableCell>
                          <TableCell className="font-medium font-mono text-sm">
                            {invoice.xero_mapping?.xero_invoice_number || invoice.invoice_no}
                          </TableCell>
                          <TableCell>{invoice.branch?.name || 'Main'}</TableCell>
                          <TableCell className="font-mono">{formatCurrency(invoice.total)}</TableCell>
                          <TableCell>
                            <Badge variant={invoice.paid ? "success" : "destructive"}>
                              {invoice.paid ? 'Paid' : 'Unpaid'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}